/**
 * TOSS Web Application - Sidebar account block
 *
 * The internal application's half of the shared account footer (`components/sidebar/SidebarAccount`):
 * this file decides what is in the menu, that one draws the row and owns the menu's opening.
 *
 * Two things changed with the move on 2026-08-26, and both were defects rather than taste:
 *
 *  - **It opens on click.** The flyout used to open on `mouseenter` after 120 ms — it fired while
 *    the pointer was on its way somewhere else, and on the tablet, where this rail is permanently
 *    collapsed, there is no hover, so the block could not be opened at all.
 *  - **«Выйти» is in the menu, not beside the name.** Expanded, the row was a link to the profile
 *    with a logout button next to it: the destructive action of the pair was the one always on
 *    screen, one stray click from ending the session.
 *
 * `logoutWithPushCleanup()` stays here and is the reason this is not shared with the portal: it
 * unregisters this device's FCM token, and the portal registers none (decision Р12).
 */

import { Link } from 'react-router-dom';
import { useCurrentUser, logoutWithPushCleanup } from '@/store/authStore';
import { ROUTES } from '@/constants';
import { SidebarAccount, sidebarAccountItemClass } from '@/components/sidebar';
import { User, LogOut } from 'lucide-react';

/**
 * User account block at the bottom of the sidebar.
 * Expanded: avatar + name, the whole row opening a menu (profile / logout).
 * Collapsed: the avatar is the trigger, and the tooltip carries name and email.
 */
export function SidebarAccountBlock({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element | null {
  const user = useCurrentUser();

  if (!user) return null;

  const handleLogout = async (): Promise<void> => {
    // Unregister this device's FCM token (best-effort) then clear local state.
    await logoutWithPushCleanup();
  };

  return (
    <SidebarAccount
      fullName={user.fullName || 'Пользователь'}
      photoUrl={user.photoUrl}
      secondaryLabel={user.email ?? undefined}
      collapsed={collapsed}
    >
      {(close) => (
        <>
          <Link
            to={ROUTES.profile.view}
            role="menuitem"
            className={sidebarAccountItemClass}
            onClick={() => {
              close();
              onNavigate?.();
            }}
          >
            <User
              className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
              aria-hidden="true"
            />
            Профиль
          </Link>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              void handleLogout();
            }}
            className={`${sidebarAccountItemClass} border-t border-[var(--color-border-light)]`}
          >
            <LogOut
              className="w-4 h-4 flex-shrink-0 text-[var(--color-text-secondary)]"
              aria-hidden="true"
            />
            Выйти
          </button>
        </>
      )}
    </SidebarAccount>
  );
}

export default SidebarAccountBlock;
